import { useEffect, useState, type ReactNode } from 'react';
import {
  Button,
  Chip,
  Description,
  Modal,
  Tabs,
  useOverlayState,
} from '@heroui/react';
import {
  Check,
  ChevronRight,
  Compass,
  Download,
  MoreVertical,
  Plus,
  Smartphone,
} from 'lucide-react';
import { AppBrandMark } from '../../../components/ui/app-brand-mark';
import { secondaryPageTabClassName } from '../../../components/ui/page-tab-classes';
import { showSuccess } from '../../../lib/toast';
import { usePwaInstall } from '../hooks/use-pwa-install';
import type { InstallPlatform } from '../utils/pwa-install';

type GuideTab = 'ios' | 'android' | 'desktop';

function defaultGuideTab(platform: InstallPlatform): GuideTab {
  if (platform === 'ios' || platform === 'android') return platform;
  return 'desktop';
}

function IosShareGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden>
      <path
        d="M12 3v11"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M8 7l4-4 4 4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6 12v6.5A2.5 2.5 0 0 0 8.5 21h7a2.5 2.5 0 0 0 2.5-2.5V12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function HomeScreenPreview() {
  return (
    <div className="w-[108px] shrink-0">
      <div className="rounded-[1.6rem] border-[4px] border-zinc-800 bg-zinc-900 p-[4px] shadow-xl shadow-black/25">
        <div className="overflow-hidden rounded-[1.2rem] bg-gradient-to-b from-sky-800 via-zinc-900 to-zinc-950 px-2 pb-2 pt-1">
          <div className="mx-auto mb-2 h-3 w-10 rounded-full bg-black" />
          <div className="flex justify-center gap-1.5">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="size-6 rounded-lg bg-white/12" />
            ))}
          </div>
          <div className="mt-2.5 flex flex-col items-center gap-1">
            <div className="relative">
              <span className="absolute -inset-1 animate-pulse rounded-[0.8rem] bg-accent/50" />
              <AppBrandMark iconSize={16} className="relative size-8 rounded-[0.7rem]" />
            </div>
            <p className="text-[8px] font-medium tracking-wide text-white/90">Link Park</p>
          </div>
          <div className="mt-2 flex justify-center gap-1 rounded-xl bg-white/10 px-1.5 py-1">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="size-4 rounded-md bg-white/25" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function SafariChrome({ highlightShare = false }: { highlightShare?: boolean }) {
  return (
    <div className="overflow-hidden rounded-xl border border-separator bg-surface-secondary">
      <div className="flex items-center gap-1.5 border-b border-separator px-2.5 py-1.5">
        <span className="size-2 rounded-full bg-red-400/80" />
        <span className="size-2 rounded-full bg-amber-400/80" />
        <span className="size-2 rounded-full bg-emerald-400/80" />
        <div className="ml-1 flex flex-1 items-center justify-center rounded-md bg-surface px-2 py-1 text-[10px] text-muted">
          linkpark.app
        </div>
      </div>
      <div className="flex items-center justify-around px-2 py-2 text-muted">
        <span className="text-xs">‹</span>
        <span className="text-xs">›</span>
        <span
          className={[
            'flex size-8 items-center justify-center rounded-lg',
            highlightShare ? 'bg-accent/15 text-accent ring-2 ring-accent/40' : '',
          ].join(' ')}
        >
          <IosShareGlyph className="size-5" />
        </span>
        <Compass size={15} />
        <span className="text-[11px] font-medium">Aa</span>
      </div>
    </div>
  );
}

function ShareSheetPreview() {
  return (
    <div className="overflow-hidden rounded-xl border border-separator bg-surface-secondary p-2">
      <div className="space-y-1.5">
        <div className="h-7 rounded-lg bg-surface" />
        <div className="h-7 rounded-lg bg-surface" />
        <div className="flex items-center gap-2 rounded-lg bg-accent/15 px-2.5 py-2 ring-1 ring-accent/40">
          <span className="flex size-7 items-center justify-center rounded-md bg-accent text-accent-foreground">
            <Plus size={14} strokeWidth={2.5} />
          </span>
          <span className="text-xs font-semibold text-foreground">Adicionar à Tela de Início</span>
        </div>
        <div className="h-7 rounded-lg bg-surface" />
      </div>
    </div>
  );
}

function ChromeMenuPreview() {
  return (
    <div className="overflow-hidden rounded-xl border border-separator bg-surface-secondary">
      <div className="flex items-center gap-2 border-b border-separator px-2.5 py-2">
        <div className="h-6 flex-1 rounded-full bg-surface text-center text-[10px] leading-6 text-muted">
          linkpark.app
        </div>
        <span className="flex size-7 items-center justify-center rounded-md bg-accent/15 text-accent ring-2 ring-accent/40">
          <MoreVertical size={16} />
        </span>
      </div>
      <div className="space-y-1 p-2">
        <div className="h-7 rounded-lg bg-surface" />
        <div className="flex items-center gap-2 rounded-lg bg-accent/15 px-2.5 py-2 ring-1 ring-accent/40">
          <Download size={14} className="text-accent" />
          <span className="text-xs font-semibold">Instalar aplicativo</span>
        </div>
        <div className="h-7 rounded-lg bg-surface" />
      </div>
    </div>
  );
}

function DesktopChromePreview() {
  return (
    <div className="overflow-hidden rounded-xl border border-separator bg-surface-secondary">
      <div className="flex items-center gap-1.5 px-2.5 py-2">
        <span className="size-2 rounded-full bg-red-400/80" />
        <span className="size-2 rounded-full bg-amber-400/80" />
        <span className="size-2 rounded-full bg-emerald-400/80" />
        <div className="ml-1 h-6 flex-1 rounded-full bg-surface text-center text-[10px] leading-6 text-muted">
          linkpark.app
        </div>
        <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-accent/15 text-accent ring-2 ring-accent/40">
          <Download size={14} />
        </span>
      </div>
    </div>
  );
}

function GuideStep({
  step,
  title,
  children,
  illustration,
}: {
  step: number;
  title: string;
  children: ReactNode;
  illustration?: ReactNode;
}) {
  return (
    <li className="flex gap-3">
      <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-bold text-accent-foreground">
        {step}
      </span>
      <div className="min-w-0 flex-1 space-y-2">
        <div>
          <p className="font-semibold leading-snug text-foreground">{title}</p>
          <p className="mt-0.5 text-sm leading-relaxed text-muted">{children}</p>
        </div>
        {illustration}
      </div>
    </li>
  );
}

export function InstallAppCta() {
  const { installed, platform, canPrompt, promptInstall } = usePwaInstall();
  const state = useOverlayState();
  const [tab, setTab] = useState<GuideTab>('ios');
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    if (!state.isOpen) return;
    setTab(defaultGuideTab(platform));
  }, [state.isOpen, platform]);

  useEffect(() => {
    if (!state.isOpen) return;
    const body = document.querySelector<HTMLElement>('[role="dialog"] .modal__body');
    body?.scrollTo({ top: 0 });
  }, [tab, state.isOpen]);

  if (installed) return null;

  const handleCtaClick = async () => {
    if (canPrompt) {
      const accepted = await promptInstall();
      if (accepted) {
        showSuccess('Pronto. Procure o ícone do Link Park na tela inicial.');
      }
      return;
    }
    state.open();
  };

  const handleNativeInstall = async () => {
    setInstalling(true);
    try {
      const accepted = await promptInstall();
      if (accepted) {
        showSuccess('Pronto. Procure o ícone do Link Park na tela inicial.');
        state.close();
      }
    } finally {
      setInstalling(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => void handleCtaClick()}
        className="flex w-full items-center gap-3 rounded-2xl border border-separator bg-surface-secondary px-3.5 py-3 text-left transition-colors hover:border-accent/40 hover:bg-surface-tertiary"
      >
        <span className="relative flex size-11 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent">
          <Smartphone size={22} />
          <span className="absolute -bottom-1 -right-1 flex size-5 items-center justify-center rounded-full bg-accent text-accent-foreground ring-2 ring-surface-secondary">
            <Download size={11} strokeWidth={2.75} />
          </span>
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-semibold text-foreground">Instalar aplicativo</span>
          <span className="mt-0.5 block text-sm text-muted">
            Atalho na tela inicial · celular e computador
          </span>
        </span>
        <ChevronRight size={18} className="shrink-0 text-muted" />
      </button>

      <Modal state={state}>
        <Modal.Backdrop isDismissable className="z-[200]">
          <Modal.Container placement="center" className="z-[200]">
            <Modal.Dialog className="w-full max-w-md">
              <Modal.Header className="border-b border-separator pb-3">
                <div className="flex flex-col gap-1 pr-8">
                  <Modal.Heading>Instalar o Link Park</Modal.Heading>
                  <Description>
                    Atalho na tela inicial, em tela cheia — sem App Store nem Play Store.
                  </Description>
                </div>
                <Modal.CloseTrigger />
              </Modal.Header>

              <Modal.Body className="flex flex-col gap-4 py-4">
                <div className="flex items-center gap-3 rounded-2xl bg-surface-secondary px-3 py-2.5">
                  <HomeScreenPreview />
                  <p className="min-w-0 text-sm leading-relaxed text-muted">
                    É o mesmo Link Park do navegador, com um toque. Sem baixar nada da loja.
                  </p>
                </div>

                {canPrompt ? (
                  <div className="rounded-2xl border border-accent/30 bg-accent/10 p-4">
                    <p className="text-sm font-semibold text-foreground">Seu navegador já pode instalar</p>
                    <p className="mt-1 text-sm text-muted">
                      Toque no botão abaixo e confirme. O ícone aparece na tela inicial.
                    </p>
                    <Button
                      variant="primary"
                      className="mt-3 w-full"
                      isDisabled={installing}
                      onPress={() => void handleNativeInstall()}
                    >
                      <Download size={16} />
                      Instalar agora
                    </Button>
                  </div>
                ) : null}

                <Tabs
                  selectedKey={tab}
                  onSelectionChange={(key) => {
                    const next = String(key);
                    if (next === 'ios' || next === 'android' || next === 'desktop') setTab(next);
                  }}
                >
                  <Tabs.ListContainer>
                    <Tabs.List aria-label="Onde instalar" className="w-full p-1.5">
                      <Tabs.Tab id="desktop" className={secondaryPageTabClassName}>
                        Computador
                        <Tabs.Indicator />
                      </Tabs.Tab>
                      <Tabs.Tab id="ios" className={secondaryPageTabClassName}>
                        iPhone
                        <Tabs.Indicator />
                      </Tabs.Tab>
                      <Tabs.Tab id="android" className={secondaryPageTabClassName}>
                        Android
                        <Tabs.Indicator />
                      </Tabs.Tab>
                    </Tabs.List>
                  </Tabs.ListContainer>
                </Tabs>

                {tab === 'desktop' ? (
                  <ol className="flex flex-col gap-5">
                    <GuideStep
                      step={1}
                      title="Abra no Chrome ou no Edge"
                      illustration={
                        <Chip size="sm" variant="soft">
                          No Safari do Mac: Arquivo → Adicionar ao Dock
                        </Chip>
                      }
                    >
                      Fique nesta página. O atalho vira uma janela do Link Park, sem a barra do
                      navegador.
                    </GuideStep>
                    <GuideStep
                      step={2}
                      title="Clique em instalar na barra de endereço"
                      illustration={<DesktopChromePreview />}
                    >
                      É o ícone de download à direita da URL. Em alguns navegadores ele só aparece
                      depois de uns segundos nesta tela.
                    </GuideStep>
                    <GuideStep step={3} title="Ou use o menu">
                      Clique nos três pontinhos → Instalar Link Park (ou Instalar aplicativo) e
                      confirme.
                    </GuideStep>
                    <GuideStep step={4} title="Pronto — abra pelo ícone">
                      O Link Park vai para o Dock, o menu Iniciar ou a área de trabalho. Da próxima
                      vez, abre como um app.
                    </GuideStep>
                  </ol>
                ) : null}

                {tab === 'ios' ? (
                  <ol className="flex flex-col gap-5">
                    <GuideStep
                      step={1}
                      title="Abra este site no Safari"
                      illustration={
                        <Chip size="sm" variant="soft">
                          Não use o Chrome no iPhone
                        </Chip>
                      }
                    >
                      No iPhone e no iPad, só o Safari consegue colocar o app na tela inicial.
                    </GuideStep>
                    <GuideStep
                      step={2}
                      title="Toque em Compartilhar"
                      illustration={<SafariChrome highlightShare />}
                    >
                      É o quadrado com a seta para cima, na barra de baixo do Safari.
                    </GuideStep>
                    <GuideStep
                      step={3}
                      title="Adicionar à Tela de Início"
                      illustration={<ShareSheetPreview />}
                    >
                      Role a lista até achar essa opção. Depois toque em Adicionar no canto
                      superior direito.
                    </GuideStep>
                    <GuideStep step={4} title="Pronto — abra pelo ícone">
                      O Link Park aparece na tela inicial, como qualquer outro app. Toque nele
                      para entrar sem a barra do Safari.
                    </GuideStep>
                  </ol>
                ) : null}

                {tab === 'android' ? (
                  <ol className="flex flex-col gap-5">
                    <GuideStep step={1} title="Abra no Chrome">
                      Use o Chrome ou o navegador padrão do celular. Fique nesta página de
                      login (ou em qualquer tela do Link Park).
                    </GuideStep>
                    <GuideStep
                      step={2}
                      title="Abra o menu"
                      illustration={<ChromeMenuPreview />}
                    >
                      Toque nos três pontinhos no canto superior direito.
                    </GuideStep>
                    <GuideStep step={3} title="Instalar aplicativo">
                      Toque em Instalar aplicativo — em alguns celulares aparece como Adicionar
                      à tela inicial. Confirme na janela que abrir.
                    </GuideStep>
                    <GuideStep step={4} title="Pronto — abra pelo ícone">
                      O atalho fica na tela inicial. Da próxima vez, o Link Park abre em tela
                      cheia, como um app de verdade.
                    </GuideStep>
                  </ol>
                ) : null}
              </Modal.Body>

              <Modal.Footer className="flex items-center justify-between gap-2 border-t border-separator pt-3">
                <p className="text-xs text-muted">Leva menos de um minuto</p>
                <Button variant="ghost" onPress={state.close}>
                  <Check size={16} />
                  Entendi
                </Button>
              </Modal.Footer>
            </Modal.Dialog>
          </Modal.Container>
        </Modal.Backdrop>
      </Modal>
    </>
  );
}
